import { useGuestLocationDraft } from './use-guest-location-draft';
import { IconChevronRight, IconCurrentLocation } from '@tabler/icons-react-native';
import { Adjustable } from 'app/design/primitives/adjustable';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { SearchInput } from 'app/design/primitives/input/search';
import { Map } from 'app/design/primitives/map';
import { Text } from 'app/design/primitives/text';
import { DEFAULT_COUNTRY } from 'app/lib/language';
import {
  type ReverseGeocodeAddressResponse,
  useGeocodeAddressSearch,
  useReverseGeocodeAddress,
} from 'app/network/address';
import { createTheme as createDSTheme } from 'app/shopify';
import type { SpecificNavigationProp } from 'app/types/navigation';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator } from 'react-native';
import Toast from 'react-native-toast-message';

const theme = createDSTheme();

interface GuestLocationFilterContentProps {
  draftState?: ReturnType<typeof useGuestLocationDraft>;
  navigation: SpecificNavigationProp<'all-listings'>;
  onApplied?: () => void;
  showActions?: boolean;
}

export function GuestLocationFilterContent(props: GuestLocationFilterContentProps) {
  if (props.draftState) return <GuestLocationDraftContent {...props} draftState={props.draftState} />;
  return <UncontrolledGuestLocationFilterContent {...props} />;
}

function UncontrolledGuestLocationFilterContent(props: GuestLocationFilterContentProps) {
  const draftState = useGuestLocationDraft();
  return <GuestLocationDraftContent {...props} draftState={draftState} />;
}

function GuestLocationDraftContent({
  draftState,
  navigation,
  onApplied,
  showActions = true,
}: GuestLocationFilterContentProps & { draftState: ReturnType<typeof useGuestLocationDraft> }) {
  const { t } = useTranslation('all-listings');
  const [locating, setLocating] = useState(false);
  const {
    appliedLatitude,
    appliedLongitude,
    defaultLocation,
    draftAddress,
    draftDistance,
    draftLocation,
    setDraftAddress,
    setDraftDistance,
    setDraftLocation,
  } = draftState;

  const geocodeAddress = useGeocodeAddressSearch({
    onError: () => Toast.show({ type: 'error', text1: t('guest.addressError') }),
    onSuccess: ({ city, latitude, longitude, postalCode }, { address }) => {
      setDraftLocation({ latitude, longitude });
      setDraftAddress([city, postalCode].filter(Boolean).join('-') || address);
    },
  });
  const reverseGeocodeAddress = useReverseGeocodeAddress();

  const reset = () => {
    if (!defaultLocation) return;
    setDraftDistance(defaultLocation.searchDistance);
    setDraftLocation({ latitude: defaultLocation.latitude, longitude: defaultLocation.longitude });
    setDraftAddress('');
  };

  const applyMyPosition = (location: { latitude: number; longitude: number }, addressLabel = t('guest.myPosition')) => {
    setDraftLocation(location);
    setDraftAddress(addressLabel);
    setLocating(false);
    if (showActions) {
      navigation.setParams({
        searchCenterLatitude: location.latitude,
        searchCenterLongitude: location.longitude,
        searchDistance: draftDistance,
        searchLocationLabel: addressLabel,
      });
      onApplied?.();
    }
  };

  const useMyPosition = () => {
    const geolocation = (globalThis.navigator as unknown as { geolocation?: Geolocation }).geolocation;
    if (!geolocation) {
      Toast.show({ type: 'error', text1: t('guest.locationError') });
      return;
    }
    setLocating(true);
    geolocation.getCurrentPosition(
      ({ coords }) => {
        const location = { latitude: coords.latitude, longitude: coords.longitude };
        reverseGeocodeAddress.mutate(location, {
          onSuccess: (address) =>
            applyMyPosition(location, getReverseGeocodedAddressLabel(address) ?? t('guest.myPosition')),
          onError: () => applyMyPosition(location),
        });
      },
      () => {
        setLocating(false);
        Toast.show({ type: 'error', text1: t('guest.locationError') });
      },
      { enableHighAccuracy: false, timeout: 10000 },
    );
  };

  const applyFilters = () => {
    const location =
      draftLocation ??
      (appliedLatitude !== undefined && appliedLongitude !== undefined
        ? { latitude: appliedLatitude, longitude: appliedLongitude }
        : undefined);
    if (!location) return;

    navigation.setParams({
      searchCenterLatitude: location.latitude,
      searchCenterLongitude: location.longitude,
      searchDistance: draftDistance,
      searchLocationLabel: draftAddress.trim() || undefined,
    });
    onApplied?.();
  };

  const searchAddress = (address: string) => {
    if (!address || geocodeAddress.isPending) return;
    setDraftAddress(address);
    geocodeAddress.mutate({ address, country: DEFAULT_COUNTRY });
  };

  const distance = draftDistance;
  const mapRegion = draftLocation ? getGuestLocationRegion(draftLocation, distance) : undefined;
  const isLocationLookupPending = locating || geocodeAddress.isPending || reverseGeocodeAddress.isPending;

  return (
    <Column gap="spacing.general.x4">
      <Text fontSize={18} fontWeight="600">
        {t('guest.locationTitle')}
      </Text>

      <Column gap="spacing.general.x2">
        <SearchInput
          editable={!isLocationLookupPending}
          fieldBorderWidth={1.5}
          initialValue={draftAddress}
          onChangeText={(address) => {
            setDraftAddress(address);
            setDraftLocation(undefined);
          }}
          onSubmit={searchAddress}
          placeholder={t('guest.postalCode')}
          right={geocodeAddress.isPending ? <ActivityIndicator size="small" /> : undefined}
          submitIconVariant="plain"
          submitOnEmptyChange={false}
        />
        <Button
          disabled={isLocationLookupPending}
          flexDirection="row"
          onPress={useMyPosition}
          style={{ minHeight: 56, width: '100%' }}
          variant="plain"
        >
          <IconCurrentLocation
            color={theme.colors['icon.primary']}
            strokeWidth={ICON_STROKE_WIDTH}
            size={24}
            style={{ marginRight: theme.spacing['spacing.general.x4'] }}
          />
          <Column flex={1}>
            <Text fontSize={16} fontWeight="600">
              {locating ? t('guest.locating') : t('guest.myPosition')}
            </Text>
            <Text color="text.secondary" fontSize={14}>
              {t('guest.myPositionHint')}
            </Text>
          </Column>
          <IconChevronRight color={theme.colors['icon.primary']} strokeWidth={ICON_STROKE_WIDTH} size={22} />
        </Button>
      </Column>

      <Column gap="spacing.general.x2">
        <Text fontSize={16} fontWeight="600">
          {t('guest.distanceTitle')}
        </Text>
        {defaultLocation ? (
          <Adjustable
            disabled={isLocationLookupPending}
            label={(value) => t('guest.distance', { distance: Math.round(value / 1000) })}
            labelOnMax={t('guest.maximumDistance', {
              distance: Math.round(defaultLocation.maxSearchDistance / 1000),
            })}
            max={defaultLocation.maxSearchDistance}
            min={1000}
            onChange={setDraftDistance}
            onSlidingComplete={setDraftDistance}
            paddingHorizontal="spacing.general.0"
            step={1000}
            value={distance}
          />
        ) : null}
      </Column>

      {draftLocation && mapRegion ? (
        <Column borderRadius="radius.lg" height={235} overflow="hidden" style={{ pointerEvents: 'none' }}>
          <Map
            height="100%"
            pitchEnabled={false}
            region={mapRegion}
            rotateEnabled={false}
            scrollEnabled={false}
            showsMyLocationButton={false}
            width="100%"
            zoomControlEnabled={false}
            zoomEnabled={false}
          >
            <Map.Circle center={draftLocation} radius={distance} />
            <Map.Marker coordinate={draftLocation} />
          </Map>
        </Column>
      ) : null}

      {showActions ? (
        <Row gap="spacing.general.x2" justifyContent="flex-end">
          <Button
            disabled={isLocationLookupPending}
            onPress={reset}
            style={{ height: 40, paddingHorizontal: 12 }}
            variant="plain"
          >
            <Text color="text.brand" fontSize={14} fontWeight="600">
              {t('guest.reset')}
            </Text>
          </Button>
          <Button
            disabled={isLocationLookupPending || !draftLocation}
            onPress={applyFilters}
            style={{ borderRadius: 20, height: 40, paddingHorizontal: 20 }}
            variant="primary"
          >
            <Text color="white" fontSize={14} fontWeight="600">
              {t('guest.apply')}
            </Text>
          </Button>
        </Row>
      ) : null}
    </Column>
  );
}

const METERS_PER_DEGREE_LATITUDE = 111_320;
const GUEST_MAP_PADDING = 1.25;

function getGuestLocationRegion(location: { latitude: number; longitude: number }, radius: number) {
  const latitudeDelta = (GUEST_MAP_PADDING * 2 * Math.max(radius, 1000)) / METERS_PER_DEGREE_LATITUDE;
  const longitudeDelta = latitudeDelta / Math.max(Math.cos((location.latitude * Math.PI) / 180), 0.1);

  return { ...location, latitudeDelta, longitudeDelta };
}

function getReverseGeocodedAddressLabel(address: ReverseGeocodeAddressResponse): string | undefined {
  return [address.city, address.postalCode].filter(Boolean).join(', ') || undefined;
}
